import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DashboardState,
  TopicExplorerOpenState,
  Annotation,
} from "../../interfaces/DashboardState";
import { MosaicDirection, MosaicNode } from "react-mosaic-component";
import { fetchLogMetadata, fetchSession } from "./sessionThunks";

const colorPaletteForDarkBackground = [
  "#FF6F61",
  "#AED9E0",
  "#F7DC6F",
  "#77DD77",
  "#DA70D6",
  "#64B5F6",
  "#FFB347",
  "#B2EBF2",
];

const initialState: DashboardState = {
  logId: null,
  name: "",
  dataSources: {},
  layout: "Pane 1",
  panes: {
    "Pane 1": {},
  },
  activePaneId: null,
  hoverBarPosition: null,
  playBarPosition: 0,
  startTime: 0,
  endTime: 0,
  topicExplorerOpenState: {},
  isIncidentView: false,
  incidentMemberSessions: [],
  // annotations per plotId
  annotations: {},
};

const replaceNode = (
  root: MosaicNode<string>,
  targetId: string,
  newNode: MosaicNode<string>,
): MosaicNode<string> => {
  if (root === targetId) {
    return newNode;
  } else if (typeof root === "object") {
    return {
      ...root,
      first: replaceNode(root.first, targetId, newNode),
      second: replaceNode(root.second, targetId, newNode),
    };
  }
  return root;
};

const removeMosaicPane = (
  id: string,
  node: MosaicNode<string>,
): MosaicNode<string> | null => {
  if (typeof node === "string") {
    return node === id ? null : node;
  }

  const newFirst = removeMosaicPane(id, node.first);
  const newSecond = removeMosaicPane(id, node.second);

  if (!newFirst && !newSecond) return null;
  if (!newFirst) return newSecond;
  if (!newSecond) return newFirst;

  return { direction: node.direction, first: newFirst, second: newSecond };
};

const getHighestPaneNumber = (node: MosaicNode<string>): number => {
  if (!node) return 0;

  const traverse = (n: MosaicNode<string>): number[] => {
    if (typeof n === "string") {
      const match = n.match(/Pane (\d+)/);
      return match ? [parseInt(match[1], 10)] : [];
    }

    return [...traverse(n.first), ...traverse(n.second)];
  };

  const numbers = traverse(node);
  return numbers.length > 0 ? Math.max(...numbers) : 0;
};

const generateTitle = (mosaicNode: MosaicNode<string>) => {
  const highestNumber = getHighestPaneNumber(mosaicNode);
  return `Pane ${highestNumber + 1}`;
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    addPane: (
      state,
      action: PayloadAction<{
        parentPaneId: string;
        direction: MosaicDirection;
      }>,
    ) => {
      const { parentPaneId, direction } = action.payload;
      const newPaneId = generateTitle(state.layout);
      return {
        ...state,
        panes: {
          ...state.panes,
          [newPaneId]: {},
        },
        layout: replaceNode(state.layout, parentPaneId, {
          direction,
          first: parentPaneId,
          second: newPaneId,
        }),
      };
    },
    addPlot: (
      state,
      action: PayloadAction<{
        paneId: string;
        topicName: string;
        field: string;
        timestamps: number[];
        data: number[] | string[];
        sourceSessionId?: string;
      }>,
    ) => {
      const { paneId, topicName, field, timestamps, data, sourceSessionId } =
        action.payload;
      const source = sourceSessionId || state.logId || "";
      const plotId = `${source}.${topicName}.${field}`;

      if (!state.panes[paneId]) {
        state.panes[paneId] = {};
      }

      if (state.dataSources[plotId]) {
        // DataSource already exists, just add subscriber if not already present
        const dataSource = state.dataSources[plotId];
        if (!dataSource.subscribers.includes(paneId)) {
          dataSource.subscribers.push(paneId);
        }
        if (!state.panes[paneId][plotId]) {
          const numPlots = Object.keys(state.panes[paneId]).length;
          const index = numPlots % colorPaletteForDarkBackground.length;
          state.panes[paneId][plotId] = {
            color: colorPaletteForDarkBackground[index],
            visible: true,
          };
        }
        return state;
      } else {
        if (!state.panes[paneId][plotId]) {
          const numPlots = Object.keys(state.panes[paneId]).length;
          const index = numPlots % colorPaletteForDarkBackground.length;

          return {
            ...state,
            panes: {
              ...state.panes,
              [paneId]: {
                ...state.panes[paneId],
                [plotId]: {
                  color: colorPaletteForDarkBackground[index],
                  visible: true,
                },
              },
            },
            dataSources: {
              ...state.dataSources,
              [plotId]: {
                topic: topicName,
                field: field,
                data: data,
                timestamps: timestamps,
                subscribers: [paneId],
                sourceSessionId: sourceSessionId || state.logId || undefined,
              },
            },
          };
        }
        return state;
      }
    },
    addPlots: (
      state,
      action: PayloadAction<{
        dataSources: Record<
          string,
          { topic: string; field: string; subscribers: string[] }
        >;
      }>,
    ) => {
      const dataSources = action.payload.dataSources;
      Object.keys(dataSources).forEach((legacyKey) => {
        const { topic, field, subscribers } = dataSources[legacyKey];
        // Assume current session as source for restored state when missing
        const source = state.logId || "";
        const plotId = `${source}.${topic}.${field}`;
        subscribers.forEach((paneId) => {
          if (!state.panes[paneId]) {
            state.panes[paneId] = {};
          }

          if (state.dataSources[plotId]) {
            const ds = state.dataSources[plotId];
            if (!ds.subscribers.includes(paneId)) {
              ds.subscribers.push(paneId);
            }
            if (!state.panes[paneId][plotId]) {
              const numPlots = Object.keys(state.panes[paneId]).length;
              const index = numPlots % colorPaletteForDarkBackground.length;
              state.panes[paneId][plotId] = {
                color: colorPaletteForDarkBackground[index],
                visible: true,
              };
            }
          } else {
            if (!state.panes[paneId][plotId]) {
              const numPlots = Object.keys(state.panes[paneId]).length;
              const index = numPlots % colorPaletteForDarkBackground.length;
              state.panes[paneId][plotId] = {
                color: colorPaletteForDarkBackground[index],
                visible: true,
              };
              state.dataSources[plotId] = {
                topic,
                field,
                data: [],
                timestamps: [],
                subscribers: [paneId],
                sourceSessionId: source,
              };
            }
          }
        });
      });
    },
    addValuesToSources: (
      state,
      action: PayloadAction<{
        key: string;
        data: number[] | string[];
        timestamp: number[];
      }>,
    ) => {
      const { key, data, timestamp } = action.payload;
      state.dataSources[key].data = data;
      state.dataSources[key].timestamps = timestamp;
    },
    togglePlotVisibility: (
      state,
      action: PayloadAction<{ paneId: string; plotId: string }>,
    ) => {
      const { paneId, plotId } = action.payload;
      if (state.panes[paneId] && state.panes[paneId][plotId]) {
        state.panes[paneId][plotId].visible =
          !state.panes[paneId][plotId].visible;
      }
    },
    updateLayout: (state, action: PayloadAction<MosaicNode<string> | null>) => {
      state.layout = action.payload ? action.payload : initialState.layout;
    },
    updatePlotColor: (
      state,
      action: PayloadAction<{ paneId: string; plotId: string; color: string }>,
    ) => {
      const { paneId, plotId, color } = action.payload;
      if (state.panes[paneId] && state.panes[paneId][plotId]) {
        state.panes[paneId][plotId].color = color;
      }
    },
    removePane: (state, action: PayloadAction<{ paneId: string }>) => {
      const { paneId } = action.payload;
      if (state.layout === paneId) {
        state.panes = { "Pane 1": {} };
        state.dataSources = {};
        state.layout = "Pane 1";
      } else {
        if (state.panes[paneId]) {
          const layout = removeMosaicPane(paneId, state.layout);
          const plotIdsInPane = Object.keys(state.panes[paneId]);
          plotIdsInPane.forEach((plotId) => {
            if (state.dataSources[plotId]) {
              const dataSource = state.dataSources[plotId];
              if (dataSource.subscribers.includes(paneId)) {
                dataSource.subscribers = dataSource.subscribers.filter(
                  (subscriberPaneId) => subscriberPaneId !== paneId,
                );
                if (dataSource.subscribers.length === 0) {
                  delete state.dataSources[plotId];
                }
              }
            }
          });
          delete state.panes[paneId];
          state.layout = layout ? layout : state.layout;
        }
      }
    },
    removePlot: (
      state,
      action: PayloadAction<{ paneId: string; plotId: string }>,
    ) => {
      const { paneId, plotId } = action.payload;
      // Always remove from pane if present
      if (state.panes[paneId] && state.panes[paneId][plotId]) {
        delete state.panes[paneId][plotId];
      }
      // If a dataSource exists, drop the subscriber and prune when no subscribers remain
      const dataSource = state.dataSources[plotId];
      if (dataSource) {
        if (dataSource.subscribers.includes(paneId)) {
          dataSource.subscribers = dataSource.subscribers.filter(
            (subscriberPaneId) => subscriberPaneId !== paneId,
          );
        }
        if (dataSource.subscribers.length === 0) {
          delete state.dataSources[plotId];
        }
      }
    },
    clearAllPanes: (state) => {
      state.panes = { "Pane 1": {} };
      state.dataSources = {};
      state.layout = "Pane 1";
      state.annotations = {};
    },
    updateHoverBarPosition: (state, action: PayloadAction<number | null>) => {
      state.hoverBarPosition = action.payload;
    },
    updatePlayBarPosition: (state, action: PayloadAction<number>) => {
      state.playBarPosition = action.payload;
    },
    setLogId: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        logId: action.payload,
      };
    },
    setChannels: () => {
      // No-op in lean mode; kept for backward compatibility for now
    },
    setActivePaneId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        activePaneId: action.payload,
      };
    },
    setStartEndTime: (
      state,
      action: PayloadAction<{
        startTime: number;
        endTime: number;
      }>,
    ) => {
      return {
        ...state,
        startTime: action.payload.startTime,
        endTime: action.payload.endTime,
      };
    },
    setTopicExplorerOpenState(
      state,
      action: PayloadAction<TopicExplorerOpenState>,
    ) {
      state.topicExplorerOpenState = action.payload;
    },
    sessionConfigLoaded(state, action) {
      state.layout = action.payload.layout;
      state.panes = action.payload.panes;
      // Also load annotations if provided
      if (action.payload.annotations) {
        state.annotations = action.payload.annotations as Record<
          string,
          Annotation[]
        >;
      }
    },
    // Annotation reducers
    addPointAnnotation: (
      state,
      action: PayloadAction<{
        plotId: string;
        timestamp: number;
        label?: string;
        description?: string;
        color?: string;
        scope?: "metric" | "pane" | "session";
      }>,
    ) => {
      const { plotId, timestamp, label, description, color, scope } =
        action.payload;
      if (!state.annotations) state.annotations = {};
      if (!state.annotations[plotId]) state.annotations[plotId] = [];
      (state.annotations[plotId] as Annotation[]).push({
        id: Math.random().toString(36).slice(2, 10),
        type: "point",
        timestamp: Math.round(timestamp),
        label,
        description,
        color,
        scope: scope || "metric",
      });
    },
    addIntervalAnnotation: (
      state,
      action: PayloadAction<{
        plotId: string;
        start: number;
        end: number;
        label?: string;
        description?: string;
        color?: string;
        id?: string;
        scope?: "metric" | "pane" | "session";
      }>,
    ) => {
      const { plotId, start, end, label, description, color, id, scope } =
        action.payload;
      if (!state.annotations) state.annotations = {};
      if (!state.annotations[plotId]) state.annotations[plotId] = [];
      const s = Math.round(Math.min(start, end));
      const e = Math.round(Math.max(start, end));
      (state.annotations[plotId] as Annotation[]).push({
        id: id || Math.random().toString(36).slice(2, 10),
        type: "interval",
        start: s,
        end: e,
        label,
        description,
        color,
        scope: scope || "metric",
      });
    },
    removeAnnotation: (
      state,
      action: PayloadAction<{ plotId: string; annotationId: string }>,
    ) => {
      const { plotId, annotationId } = action.payload;
      const list = state.annotations?.[plotId];
      if (!list) return;
      state.annotations[plotId] = list.filter((a) => a.id !== annotationId);
      if (state.annotations[plotId]!.length === 0)
        delete state.annotations[plotId];
    },
    moveAnnotation: (
      state,
      action: PayloadAction<{
        fromPlotId: string;
        toPlotId: string;
        annotationId: string;
      }>,
    ) => {
      const { fromPlotId, toPlotId, annotationId } = action.payload;
      if (fromPlotId === toPlotId) return;
      const fromList = state.annotations?.[fromPlotId];
      if (!fromList) return;
      const idx = fromList.findIndex((a) => a.id === annotationId);
      if (idx === -1) return;
      const ann = fromList[idx];
      // remove from source
      state.annotations[fromPlotId] = fromList.filter(
        (a) => a.id !== annotationId,
      );
      if (state.annotations[fromPlotId]!.length === 0)
        delete state.annotations[fromPlotId];
      // add to destination
      if (!state.annotations[toPlotId]) state.annotations[toPlotId] = [];
      (state.annotations[toPlotId] as Annotation[]).push(ann);
    },
    updateAnnotation: (
      state,
      action: PayloadAction<{
        plotId: string;
        annotationId: string;
        changes: Partial<
          Pick<Annotation, "label" | "description" | "color" | "scope">
        >;
      }>,
    ) => {
      const { plotId, annotationId, changes } = action.payload;
      const list = state.annotations?.[plotId];
      if (!list) return;
      const idx = list.findIndex((a) => a.id === annotationId);
      if (idx === -1) return;
      state.annotations[plotId] = list.map((a, i) =>
        i === idx ? ({ ...a, ...changes } as Annotation) : a,
      );
    },
    updateIntervalBounds: (
      state,
      action: PayloadAction<{
        plotId: string;
        annotationId: string;
        start?: number;
        end?: number;
      }>,
    ) => {
      const { plotId, annotationId, start, end } = action.payload;
      const list = state.annotations?.[plotId];
      if (!list) return;
      const idx = list.findIndex(
        (a) => a.id === annotationId && a.type === "interval",
      );
      if (idx === -1) return;
      const a = list[idx] as any;
      const newStart = start ?? a.start;
      const newEnd = end ?? a.end;
      const s = Math.round(Math.min(newStart, newEnd));
      const e = Math.round(Math.max(newStart, newEnd));
      a.start = s;
      a.end = e;
    },
    setAnnotations(state, action: PayloadAction<Record<string, Annotation[]>>) {
      state.annotations = action.payload || {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLogMetadata.fulfilled, (state, action) => {
        const { isIncidentView, incidentMemberSessions, ...sessionMeta } =
          action.payload;

        // Reset state before loading new session
        Object.assign(state, initialState);

        // Populate state from thunk payload
        state.isIncidentView = isIncidentView;
        state.incidentMemberSessions = incidentMemberSessions || [];
        state.logId = sessionMeta.sessionId;
        state.name = sessionMeta.name;
        state.startTime = sessionMeta.startTime;
        state.endTime = sessionMeta.endTime;
        state.playBarPosition = sessionMeta.startTime;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.dataSources = action.payload.dataSources;
        if (action.payload.annotations) {
          state.annotations = action.payload.annotations as Record<
            string,
            Annotation[]
          >;
        }
      });
  },
});

export const {
  addPane,
  addPlot,
  addPlots,
  addValuesToSources,
  togglePlotVisibility,
  updateLayout,
  updatePlotColor,
  removePane,
  removePlot,
  clearAllPanes,
  updateHoverBarPosition,
  updatePlayBarPosition,
  setLogId,
  setChannels,
  setActivePaneId,
  setStartEndTime,
  setTopicExplorerOpenState,
  sessionConfigLoaded,
  addPointAnnotation,
  addIntervalAnnotation,
  removeAnnotation,
  moveAnnotation,
  updateAnnotation,
  updateIntervalBounds,
  setAnnotations,
} = dashboardSlice.actions;

export const selectTopicExplorerOpenState = (state: {
  dashboardReducer: DashboardState;
}): TopicExplorerOpenState => state.dashboardReducer.topicExplorerOpenState;

export const dashboardReducer = dashboardSlice.reducer;
